import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { RefreshCw, CheckCircle, ArrowUpCircle, Smartphone } from "lucide-react";

const BUILD_VERSION = process.env.REACT_APP_VERSION ||
  new Date().toLocaleDateString("id-ID", { year: "numeric", month: "long", day: "numeric" });

export default function AppVersion() {
  const [latest, setLatest] = useState(null);
  const [checking, setChecking] = useState(false);
  const [swWaiting, setSwWaiting] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [platform, setPlatform] = useState("Browser");

  useEffect(() => {
    const ua = navigator.userAgent;
    const isIOS = /iPhone|iPad/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    const isPWA = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isPWA && isIOS) setPlatform("iOS · PWA");
    else if (isPWA && isAndroid) setPlatform("Android · Chrome PWA");
    else if (isIOS) setPlatform("iOS · Safari");
    else if (isAndroid) setPlatform("Android · Chrome");
    else setPlatform("Desktop · Browser");

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg?.waiting) setSwWaiting(true);
        if (reg) {
          reg.addEventListener("updatefound", () => {
            const newSw = reg.installing;
            newSw?.addEventListener("statechange", () => {
              if (newSw.state === "installed" && navigator.serviceWorker.controller) {
                setSwWaiting(true);
              }
            });
          });
        }
      });
    }

    checkVersion();
  }, []);

  const checkVersion = useCallback(async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/version");
      setLatest(data.version);
      setLastChecked(new Date());

      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        await reg?.update();
        if (reg?.waiting) setSwWaiting(true);
      }
    } catch {}
    finally { setChecking(false); }
  }, []);

  const applyUpdate = () => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        reg?.waiting?.postMessage({ type: "SKIP_WAITING" });
        window.location.reload();
      });
    } else {
      window.location.reload();
    }
  };

  const isUpToDate = latest && !swWaiting;
  const hasUpdate = swWaiting;

  const fmtTime = (d) => d
    ? d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) + ", " +
      d.toLocaleDateString("id-ID", { day: "numeric", month: "short" })
    : "–";

  return (
    <div className="card-surface overflow-hidden">
      {hasUpdate && (
        <div className="flex items-center gap-3 px-5 py-3.5" style={{ background: "#FFF8F0", borderBottom: "0.5px solid #FFD299" }}>
          <ArrowUpCircle className="w-5 h-5 flex-shrink-0" style={{ color: "#FF8C00" }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#1A1422]">Pembaruan tersedia</div>
            <div className="text-xs text-[#5C5868]">
              {latest && BUILD_VERSION !== "dev" ? `Versi ${latest} siap diinstal` : "Versi baru sudah diunduh"}
            </div>
          </div>
          <button
            onClick={applyUpdate}
            className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full text-white"
            style={{ background: "#FF8C00" }}
          >
            Perbarui
          </button>
        </div>
      )}

      <div className="divide-y divide-[#E8E5EE]">
        <div className="flex items-center gap-3 px-5 py-4">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ background: "#FFF3E0" }}>
            <img src="/icon-192.png" alt="BisaUsaha" className="w-7 h-7 object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#1A1422]">BisaUsaha</div>
            <div className="text-xs text-[#8B8794]">
              Versi kamu: <span className="font-medium text-[#5C5868]">{BUILD_VERSION}</span>
            </div>
          </div>
          {isUpToDate && (
            <div className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: "#DCFCE7", color: "#166534" }}>
              <CheckCircle className="w-3 h-3" /> Terbaru
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 px-5 py-4">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#F4F2F8" }}>
            <Smartphone className="w-4 h-4 text-[#6B21A8]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#1A1422]">Platform</div>
            <div className="text-xs text-[#8B8794]">{platform}</div>
          </div>
        </div>

        <div className="flex items-center gap-3 px-5 py-4">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#F4F2F8" }}>
            <RefreshCw className={`w-4 h-4 text-[#6B21A8] ${checking ? "animate-spin" : ""}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#1A1422]">Terakhir dicek</div>
            <div className="text-xs text-[#8B8794]">{fmtTime(lastChecked)}</div>
          </div>
          <button
            onClick={checkVersion}
            disabled={checking}
            className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border"
            style={{ color: "#6B21A8", borderColor: "#E8E5EE", background: "transparent" }}
          >
            {checking ? "Mengecek..." : "Cek"}
          </button>
        </div>
      </div>
    </div>
  );
}
